// app.deviceLink — the RELAY half of device-link sign-in ("click a link, the
// agent is in"). The crypto half lives in device-link.ts (pure, no AWS); this
// module is the bucket I/O that `s3p connect`'s approve page needs:
//
//   The agent parked an ephemeral public key at
//   `.s3p/device-login/{deviceId}/request.json` and printed ONE url. The
//   approve page reads that request, shows who's asking, and on Approve seals
//   the current session's refresh token (the same thing an agent code is) to
//   the device's public key and writes `answer.json` next to it.
//
// All relay I/O rides VISITOR STS — the same anonymous credentials any
// unauthenticated tab can mint. Being signed in is required only for having a
// refresh token worth handing over. There is deliberately NO list operation:
// the unguessable deviceId is the capability, and answers are sealed.

import {
  GetObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { deviceRequestExpired, sealForDevice } from "./device-link.ts";
import type { DeviceLoginGrant, DeviceLoginRequest } from "./device-link.ts";
import type { Paths } from "./paths.ts";

export type { DeviceLoginGrant, DeviceLoginRequest } from "./device-link.ts";

export interface DeviceLinkModule {
  /** Read a pending connect request, or null when the link isn't active
   *  (already used, aged out past DEVICE_LINK_TTL_MS, or never existed). */
  getRequest(deviceId: string): Promise<DeviceLoginRequest | null>;
  /** Seal `grant` to the request's public key and publish the answer. The
   *  polling CLI decrypts it, caches a session, and deletes the relay
   *  objects. Throws when the request is gone. */
  approve(deviceId: string, grant: DeviceLoginGrant): Promise<void>;
}

export interface DeviceLinkConfig {
  /** Visitor (anonymous) S3 — the relay must work from a not-yet-signed-in tab. */
  getVisitorClient: () => Promise<S3Client>;
  bucket: string;
  bucketPrefix: string;
  paths: Paths;
}

export function createDeviceLink(cfg: DeviceLinkConfig): DeviceLinkModule {
  const checkId = (deviceId: string) => {
    if (!/^[a-zA-Z0-9_-]{16,}$/.test(deviceId)) {
      throw new Error("app.deviceLink: malformed deviceId");
    }
  };

  return {
    async getRequest(deviceId) {
      checkId(deviceId);
      try {
        const s3 = await cfg.getVisitorClient();
        const r = await s3.send(
          new GetObjectCommand({
            Bucket: cfg.bucket,
            Key: `${cfg.bucketPrefix}${cfg.paths.deviceLoginRequest(deviceId)}`,
          }),
        );
        const body = await (
          r.Body as { transformToString: () => Promise<string> }
        ).transformToString();
        if (!body) return null;
        const request = JSON.parse(body) as DeviceLoginRequest;
        // Expiry is enforced HERE rather than in the approve UI, so every
        // caller of the relay inherits it and "not active" stays one concept.
        return deviceRequestExpired(request) ? null : request;
      } catch {
        return null;
      }
    },

    async approve(deviceId, grant) {
      checkId(deviceId);
      const request = await this.getRequest(deviceId);
      if (!request) {
        throw new Error(
          "app.deviceLink.approve: no active request for this deviceId — ask the agent to run `s3p connect` again.",
        );
      }
      const box = await sealForDevice(request.pubJwk, grant);
      const s3 = await cfg.getVisitorClient();
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key: `${cfg.bucketPrefix}${cfg.paths.deviceLoginAnswer(deviceId)}`,
          Body: JSON.stringify(box),
          ContentType: "application/json",
        }),
      );
    },
  };
}
